import { FC, useEffect, useState, useCallback } from 'react'

import clsx from 'clsx'

import EmptyMessage from './EmptyMessage/EmptyMessage'
import AddComment from './AddComment/AddComment'
import Thread from './Activity/Thread'
import Log from './Activity/Log'
import Feedback from './Activity/Feedback'

import { Activity as ActivityType } from 'container/AssignmentDetails/TabContent/Activities/Activities'

import { ASSIGNMENT_STATUS } from '@pepper/utils/src/constant/enum/assignment_status'

import styles from './_.module.css'

interface Props {
  projectFormFields: BackendFormField[]
  assignmentBrief: BackendFormField[]
  activities: ActivityType[]
  refetch: {
    threads: () => void
    assignmentDetails: () => void
    feedback: () => void
  }
  assignmentId: string
  disabled?: boolean
  extraData?: {
    activeStatus: AssignmentStatus
    assignmentTitle: string
    assignmentCategory?: 'text' | 'design' | 'translation' | 'others'
    submissionFiles: Attachment[]
    googleDocFiles: {
      fileUrl: string
    }[]
    show_pepper_creator?: boolean
    fileSrcType?: string
  }
  keepSamePage: boolean
  latestAssignmentStatus: boolean
  projectCategory: ProjectCategory
  submissionText?: string | null
  hide?: boolean
}

const RecentActivity: FC<Props> = ({
  activities = [],
  refetch,
  assignmentId,
  disabled = false,
  extraData,
  keepSamePage,
  latestAssignmentStatus,
  projectCategory,
  projectFormFields,
  assignmentBrief,
  submissionText = null,
  hide = false,
}) => {
  const [latestLogDetails, setLatestLogDetails] = useState<{
    state_id: string
    created_at: string
  }>()

  useEffect(() => {
    const _latestLog = activities.find(data => data.isLatest)
    setLatestLogDetails({
      state_id: _latestLog?.state_id ?? '',
      created_at: _latestLog?.created_at ?? '',
    })
  }, [activities])

  const scrollToBottom = useCallback(() => {
    const sideExpandedList = document.getElementById('side-details-expanded')
    if (sideExpandedList) {
      sideExpandedList.scrollTo(0, sideExpandedList.offsetHeight)
    }
  }, [])

  const getActivityComponent = (
    data: ActivityType,
    lastMessageInThread: boolean,
    reworkIndex: number,
    rowIndex: number
  ) => {
    if (data.activityType === 'thread') {
      return (
        <Thread
          data={data as ThreadMessage}
          lastMessageInThread={keepSamePage && lastMessageInThread}
          scrollToBottom={scrollToBottom}
        />
      )
    } else if (data.activityType === 'log') {
      return (
        <Log
          projectFormFields={projectFormFields}
          assignmentBrief={assignmentBrief}
          data={data as Timeline}
          latestAssignmentStatus={latestAssignmentStatus}
          refetch={refetch}
          extraData={extraData}
          assignmentId={assignmentId}
          latestLogDetails={latestLogDetails}
          lastMessageInThread={keepSamePage && lastMessageInThread}
          scrollToBottom={scrollToBottom}
          projectCategory={projectCategory}
          reworkIndex={reworkIndex}
          rowIndex={rowIndex}
          submissionText={submissionText || ''}
        />
      )
    } else {
      return (
        <Feedback
          data={data as ClientFeedback}
          lastMessageInThread={keepSamePage && lastMessageInThread}
          scrollToBottom={scrollToBottom}
        />
      )
    }
  }
  const reworkIndex = activities?.findIndex(
    activity => ASSIGNMENT_STATUS.CLI_RWK.id === activity?.status_code
  )
  return (
    <section
      className={clsx(styles.recentActivityContainer, {
        [styles.recentActivitySamePageWrap]: keepSamePage,
      })}
    >
      <div id="activity-list" className={styles.activityList}>
        {activities.length === 0 ? <EmptyMessage /> : ''}
        {activities.length > 0
          ? activities.map((activity: ActivityType, index) => {
              return (
                <div className={styles.activityContainer} key={index}>
                  {getActivityComponent(
                    activity,
                    index === activities.length - 1,
                    reworkIndex,
                    index
                  )}
                </div>
              )
            })
          : ''}
      </div>
      <div id="comment-box" className={styles.commentBox}>
        {!hide && (
          <AddComment
            keepSamePage={keepSamePage}
            assignmentId={assignmentId}
            refetch={refetch.threads}
            disabled={disabled}
          />
        )}
      </div>
    </section>
  )
}

export default RecentActivity
